import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { fetchConversations, fetchMessages, sendMessage, markMessagesRead, fetchAllUsers } from '../../services/api';
import './Messages.css';

export default function Messages() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null); // partnerId
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [loading, setLoading] = useState(false);

    // For starting a new chat
    const [availableUsers, setAvailableUsers] = useState([]);
    const [showNewChatModal, setShowNewChatModal] = useState(false);

    useEffect(() => {
        loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

    useEffect(() => {
        if (activeConversation) {
            loadMessages(activeConversation);
        }
    }, [activeConversation]);

    const loadConversations = async () => {
        try {
            const data = await fetchConversations();
            setConversations(data);
            if (data.length > 0 && !activeConversation) {
                // Optionally select the first one
                // setActiveConversation(data[0].partner_id);
            }
        } catch (error) {
            console.error("Failed to load conversations", error);
        }
    };

    const loadMessages = async (partnerId) => {
        setLoading(true);
        try {
            const data = await fetchMessages(partnerId);
            setMessages(data);
            // Mark as read locally
            markMessagesRead(partnerId).catch(console.error);
        } catch (error) {
            console.error("Failed to load messages", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !activeConversation) return;

        try {
            const msg = await sendMessage({
                receiver_id: activeConversation,
                content: newMessage
            });
            setMessages(prev => [...prev, msg]);
            setNewMessage("");
            // Update conversation list last message preview
            loadConversations();
        } catch (error) {
            console.error("Failed to send message", error);
            alert(t('common.error_occurred', 'Failed to send message'));
        }
    };

    const openNewChatModal = async () => {
        try {
            setShowNewChatModal(true);
            const users = await fetchAllUsers(); // This might return too many users in a real app
            // Filter out self
            const others = users.filter(u => u.id !== user.id);
            setAvailableUsers(others);
        } catch (error) {
            console.error(error);
        }
    };

    const startChat = (user) => {
        setActiveConversation(user.id);
        setShowNewChatModal(false);
        // Check if conversation exists in list, if not add temp one or just rely on loadMessages to start fresh
        const exists = conversations.find(c => c.partner_id === user.id);
        if (!exists) {
            // we will fetch messages (empty) and render empty view
            setMessages([]);
        }
    };

    const getPartnerName = (conv) => {
        if (conv.first_name && conv.last_name) return `${conv.first_name} ${conv.last_name}`;
        return conv.email; // Fallback
    };

    // Find active partner info
    const activePartnerInfo = conversations.find(c => c.partner_id === activeConversation)
        || availableUsers.find(u => u.id === activeConversation);

    return (
        <div className="messages-container">
            <div className="conversations-sidebar">
                <div className="sidebar-header">
                    <h3>{t('messages.conversations', 'Conversations')}</h3>
                    <button className="btn-icon" onClick={openNewChatModal} title={t('messages.new_chat', 'New Chat')}>+</button>
                </div>
                <ul className="conversations-list">
                    {conversations.length === 0 && <p className="muted-text">{t('messages.no_conversations', 'No conversations yet')}</p>}
                    {conversations.map(conv => (
                        <li
                            key={conv.partner_id}
                            className={`conv-item ${activeConversation === conv.partner_id ? 'active' : ''} ${!conv.is_read && conv.last_sender_id !== user.id ? 'unread' : ''}`}
                            onClick={() => setActiveConversation(conv.partner_id)}
                        >
                            <div className="avatar">{getPartnerName(conv).charAt(0).toUpperCase()}</div>
                            <div className="conv-info">
                                <span className="conv-name">{getPartnerName(conv)}</span>
                                <span className="conv-preview">{conv.last_message}</span>
                            </div>
                            {!conv.is_read && conv.last_sender_id !== user.id && <div className="unread-dot" />}
                        </li>
                    ))}
                </ul>
            </div>

            <div className="chat-area">
                {activeConversation ? (
                    <>
                        <div className="chat-header">
                            <h3>{activePartnerInfo ? (activePartnerInfo.first_name ? `${activePartnerInfo.first_name} ${activePartnerInfo.last_name}` : activePartnerInfo.email) : 'Chat'}</h3>
                        </div>
                        <div className="messages-list">
                            {loading ? <p>Loading...</p> : (
                                messages.length === 0 ? <p className="empty-chat">{t('messages.start_conversation', 'Start the conversation!')}</p> :
                                    messages.map(msg => (
                                        <div key={msg.id} className={`message-bubble ${msg.sender_id === user.id ? 'sent' : 'received'}`}>
                                            <p>{msg.content}</p>
                                            <span className="timestamp">{new Date(msg.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                    ))
                            )}
                        </div>
                        <form className="chat-input-form" onSubmit={handleSend}>
                            <input
                                type="text"
                                placeholder={t('messages.type_placeholder', 'Type a message...')}
                                value={newMessage}
                                onChange={e => setNewMessage(e.target.value)}
                            />
                            <button type="submit" disabled={!newMessage.trim()}>{t('common.send', 'Send')}</button>
                        </form>
                    </>
                ) : (
                    <div className="no-chat-selected">
                        <p>{t('messages.select_chat', 'Select a conversation or start a new one')}</p>
                    </div>
                )}
            </div>

            {showNewChatModal && (
                <div className="modal-backdrop">
                    <div className="modal">
                        <h3>{t('messages.new_chat', 'New Chat')}</h3>
                        <div className="user-list">
                            {availableUsers.map(u => (
                                <div key={u.id} className="user-item" onClick={() => startChat(u)}>
                                    <span>{u.first_name} {u.last_name} ({u.role})</span>
                                </div>
                            ))}
                        </div>
                        <button className="btn ghost" onClick={() => setShowNewChatModal(false)}>{t('common.cancel', 'Cancel')}</button>
                    </div>
                </div>
            )}
        </div>
    );
}
